import {Flyer, FlyerItemDetails} from "../definitions/FlyerDefinitions";
import {Coupon} from "../definitions/CouponDefinitions";
import {AnalyticsService} from "../services/AnalyticsService";
import {DisplayType} from "../definitions/BackflippDefinitions";
import {i18n} from "../i18n/I18n";
import {Dialog, DialogHeader} from "../components/flipp-dialog/Dialog";
import {ToastContainer} from "../components/flipp-toast-container/ToastContainer";
import {ItemDialog} from "../components/flipp-item-dialog/ItemDialog";
import {Country, LocationService} from "../services/LocationService";
import {ClipFlyerItemButton} from "../components/flipp-clip-button/ClipFlyerItemButton";
import {FlyerItemElement} from "../components/flipp-flyer-item/FlyerItemElement";
import {FlyerItemStructureData} from "../components/flipp-data-structure/FlyerItemStructureData";
import {CouponClippingDialogContent} from "../components/flipp-coupon-clipping/CouponClipping";

function _makeTTM(item: FlyerItemDetails) {
	const location = LocationService.getInstance();
	return item.ttmUrl ? item.ttmUrl
		.replace("__POSTAL_CODE__", location.postalCode)
		.replace("__FLIPP_POSTAL_CODE__", location.postalCode)
		.replace("__FLIPP_LOCALE__", location.locale) : null;
}

function _reviewsClicked() {
	const mql = window.matchMedia("(max-width: 600px)");
	if (mql.matches) return;
	const container = document.querySelectorAll("flipp-flyer-item .container");
	const itemInfo = document.querySelectorAll("flipp-dialog.flipp-flyer-item-dialog .item-info");
	const reviews = document.querySelectorAll("flipp-dialog.flipp-flyer-item-dialog section.reviews");
	if (itemInfo.length === 0 || container.length === 0 || reviews.length === 0) return;
	const paddingTop = (container[0] as HTMLElement).offsetTop;
	const offset = (reviews[0] as HTMLElement).offsetTop;
	itemInfo[0].scrollTop = 1;
	setTimeout(() => {
		itemInfo[0].scrollTop = offset - paddingTop;
	}, 0);
}


export function makeItemPop(item: FlyerItemDetails, flyer: Flyer, coupons: Coupon[]) {
	const title = itemPopTitle(item, flyer);
	const locationService = LocationService.getInstance();

	const ttmUrl = _makeTTM(item);
	const ttmButton = ttmUrl ?
		<a href={ttmUrl} class="flipp-button" rel="nofollow"
		target="_blank"
		onClick={() => {
		AnalyticsService.getInstance().trackFlyerItem(flyer, item.id, 1);
		}}
		>
			<div>
				<span class="label">{item.ttmLabel}</span>
			</div>
		</a>
		: null;

	const getItemLabel = () => {
		switch (item.displayType) {
			case DisplayType.LINK:
				return "link";
			case DisplayType.IFRAME:
			case DisplayType.ONLYFEATUREHTML:
				return "iframe";
			case DisplayType.VIDEO:
				return "video";
			default:
				return "item";
		}
	};

	const indexed: any = {};
	if (flyer.webIndexed && coupons.length === 0) {
		indexed["data-indexed"] = true;
	}

	const unavailable = (item.displayType === DisplayType.COUPONV2 ||
		item.displayType === DisplayType.COUPONV3) && coupons.length === 0;

	const originalPriceBox = item.originalPrice ?
		<div class="original-price-info">
			<flipp-price value={item.originalPrice}/>
			<span>{i18n("you_save")}</span>
			<flipp-price value={`${Number(item.originalPrice) - Number(item.currentPrice)}`}/>
			<span>({i18n("percent_off", {
				"percent": `${Math.floor(((Number(item.originalPrice) - Number(item.currentPrice)) /
					Number(item.originalPrice)) * 100)}`
			})})</span>
		</div> : null;

	const showPrice = item.prePriceText || item.currentPrice || item.priceText;
	const priceBox = showPrice ?
		<div class="item-price-info">
			<span class="pre-price-text">{item.prePriceText}</span>
			<flipp-price value={item.currentPrice}/>
			<span class="price-text">{item.priceText}</span>
		</div> : null;

	return (<Dialog class="flipp-flyer-item-dialog" transient>
		<ToastContainer>
			<ItemDialog {...indexed} data-type={getItemLabel()} details={{
				itemImageUrl: item.imageUrl,
				merchantLogoUrl: item.merchantLogo,
				unavailable: unavailable ? {
					title: i18n("coupon_unavailable_cta"),
					message: locationService.country === Country.CANADA ?
						i18n("coupon_unavailable_limit_message") :
						i18n("coupon_unavailable_fsa_message")
				} : null
			}}>
				<span content-slot="title">{title}</span>
				{item.reviewAverageRating && item.reviewSamples.length > 0 ?
					<span content-slot="ratings" flex-horizontal>
							<button onClick={_reviewsClicked}>
								<div flex-horizontal>
									<flipp-rating value={`${item.reviewAverageRating}`}/>
									<span>({`${item.reviewSamples.length}`})</span>
								</div>
							</button>
						</span>
					:
					null
				}
				{priceBox ?
					<span content-slot="subtitle">
							<div>{priceBox}</div>
						</span>
					: null}
				{originalPriceBox ?
					<span content-slot="dealDetails">
							<div class="fade">{originalPriceBox}</div>
						</span>
					: null}
				<div content-slot="cta">
					<div class="cta-buttons" flex-horizontal>
						{ttmButton}
						<ClipFlyerItemButton
							class="flipp-button"
							classList={{"secondary": ttmButton !== null}}
							flyerId={item.flyerId} itemId={item.id}/>
					</div>
				</div>
				<div>
					<FlyerItemElement itemId={item.id}/>
				</div>
				<FlyerItemStructureData flyerItemDetails={JSON.stringify(item)}/>
			</ItemDialog>
		</ToastContainer>
	</Dialog> as any) as Dialog;
}

export function makeCouponPop(coupon: Coupon, showterms = true) {
	return (<Dialog class="coupon-pop"
					ref="dialog" id={`coupon-${coupon.id}-dialog`} transient>
		<DialogHeader>
			<div content-slot="title">{i18n("coupon_details_title")}</div>
		</DialogHeader>

		<ToastContainer>
			<CouponClippingDialogContent couponId={coupon.id} showterms={showterms}/>
		</ToastContainer>
	</Dialog> as any) as Dialog;
}

export function itemPopTitle(item: FlyerItemDetails, flyer: Flyer) {
	return item.displayType === DisplayType.LINK ?
		i18n("leaving_flipp") :
		i18n("item_details_title", {
			name: flyer.webIndexed ?
				`${flyer.merchant} ${flyer.name}` :
				item.name
		});
}
